import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Card, CardContent } from '@/shared/components/ui/card';
import { StackedBarChart } from '@/shared/components/charts';
import type { ChartMultiSeriesData } from '@/shared/components/charts';
import { formatHours } from '@/shared/utils/formatHours';
import { useMyTimeAnalysis } from '../hooks/useMyTimeAnalysis';
import { MyTimeAnalysisKpiCards } from './MyTimeAnalysisKpiCards';

export function MyTimeAnalysisSection() {
  const { t } = useTranslation('personalSummary');
  const { data, isLoading, isError, refetch } = useMyTimeAnalysis();

  if (isLoading) {
    return (
      <Card>
        <CardContent
          className="space-y-4 p-4"
          aria-hidden="true"
          data-testid="personal-summary-time-loading-skeleton"
        >
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-40 w-full" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent
          data-testid="personal-summary-time-error-message"
          className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
        >
          <p className="text-sm text-red-700 dark:text-red-400">{t('timeAnalysis.error')}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const byProject = data?.byProject ?? [];
  const byTask = data?.byTask ?? [];
  const hasData = (data?.totalEstimatedHours ?? 0) > 0 || (data?.totalTrackedHours ?? 0) > 0;

  if (!data || !hasData) {
    return (
      <Card>
        <CardContent
          data-testid="personal-summary-time-empty-state"
          className="p-4 py-8 text-center"
        >
          <p className="text-sm text-muted-foreground">{t('timeAnalysis.empty')}</p>
        </CardContent>
      </Card>
    );
  }

  const chartData: ChartMultiSeriesData = {
    categories: byProject.map((p) => p.projectName ?? '—'),
    series: [
      {
        name: t('timeAnalysis.chart.estimated'),
        values: byProject.map((p) => p.estimatedHours),
      },
      {
        name: t('timeAnalysis.chart.real'),
        values: byProject.map((p) => p.trackedHours),
      },
    ],
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 shrink-0 text-amber-500" />
          <h3 className="text-base font-semibold md:text-lg">{t('sections.timeAnalysis')}</h3>
        </div>

        <MyTimeAnalysisKpiCards data={data} />

        <div data-testid="personal-summary-time-chart">
          <p className="mb-2 text-sm font-semibold">{t('timeAnalysis.chart.title')}</p>
          <div className="h-64">
            <StackedBarChart data={chartData} grouped colors={['#6b7280', '#06b6d4']} />
          </div>
        </div>

        {byProject.length > 0 && (
          <div
            className="overflow-x-auto rounded-lg border"
            data-testid="personal-summary-time-project-table"
          >
            <table className="min-w-full table-auto text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-2 pl-4 pr-2 text-left font-medium">
                    {t('timeAnalysis.table.project')}
                  </th>
                  <th className="px-2 py-2 text-right font-medium">
                    {t('timeAnalysis.table.estimated')}
                  </th>
                  <th className="px-2 py-2 text-right font-medium">
                    {t('timeAnalysis.table.tracked')}
                  </th>
                  <th className="px-2 py-2 text-right font-medium">
                    {t('timeAnalysis.table.difference')}
                  </th>
                  <th className="px-2 py-2 pr-4 text-right font-medium">
                    {t('timeAnalysis.table.efficiency')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {byProject.map((p) => {
                  const difference = p.trackedHours - p.estimatedHours;
                  return (
                    <tr key={p.projectId} className="border-b last:border-0">
                      <td className="max-w-[160px] py-2 pl-4 pr-2">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: p.projectColor ?? '#6b7280' }}
                          />
                          <span className="min-w-0 truncate">{p.projectName ?? '—'}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right text-muted-foreground">
                        {formatHours(p.estimatedHours)}
                      </td>
                      <td className="px-2 py-2 text-right">{formatHours(p.trackedHours)}</td>
                      <td className="px-2 py-2 text-right">
                        {difference >= 0 ? '+' : '-'}
                        {formatHours(Math.abs(difference))}
                      </td>
                      <td className="px-2 py-2 pr-4 text-right font-bold text-primary">
                        {p.efficiencyPercentage.toFixed(0)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {byTask.length > 0 && (
          <div data-testid="personal-summary-time-task-efficiency">
            <p className="mb-3 text-sm font-semibold">{t('timeAnalysis.taskEfficiency.title')}</p>
            <div className="space-y-3">
              {byTask.map((task) => {
                const barPct = Math.min(task.efficiencyPercentage, 100);
                return (
                  <div key={task.taskId ?? task.taskTitle}>
                    <div className="mb-1.5 flex items-center justify-between gap-4">
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {task.taskTitle ?? '—'}
                      </span>
                      <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        <span>
                          {formatHours(task.trackedHours)}
                          {task.estimatedHours != null
                            ? ` / ${formatHours(task.estimatedHours)}`
                            : ''}
                        </span>
                        <span className="font-bold text-primary">
                          {task.efficiencyPercentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
